import { getTiktokEmbed } from "@/api/endpoints/tiktok";
import { SOURCE } from "@/constants";
import { cn } from "@/lib/utils";
import { EpisodeV4, MentionCard, MentionV4 } from "@/types/MentionCard";
import { events } from "@/utils";
import { Dialog, DialogContent, DialogTrigger } from "@components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { PlayIcon } from "lucide-react";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

type CoverProps = {
  mentionCard: MentionCard;
};

type TiktokCoverProps = {
  episodeGuid: MentionCard["episode"]["guid"];
  episodeUrl: MentionCard["episode"]["public_url"];
};

const TIKTOK_EMBED_SCRIPT_ID = "tiktok-embed";
const TIKTOK_EMBED_SCRIPT_SRC = "https://www.tiktok.com/embed.js";

function TiktokPlayer({ episodeGuid, episodeUrl }: TiktokCoverProps) {
  // Stop any current playback when opening the modal.
  useEffect(() => {
    document.dispatchEvent(events.stopAllPlayingAudio);
  }, []);

  useEffect(() => {
    let mounted = true;
    const head = document.head;
    const script = document.createElement("script");
    script.id = TIKTOK_EMBED_SCRIPT_ID;
    script.src = TIKTOK_EMBED_SCRIPT_SRC;

    const rafId = window.requestAnimationFrame(() => {
      if (!mounted) return;
      const current = document.getElementById(TIKTOK_EMBED_SCRIPT_ID);
      if (current) {
        head.replaceChild(script, current);
      } else {
        head.appendChild(script);
      }
    });

    return () => {
      mounted = false;
      cancelAnimationFrame(rafId);
    };
  }, []);

  if (!episodeGuid) {
    return null;
  }

  return (
    <div className="w-fit mx-auto">
      <blockquote
        className="tiktok-embed"
        data-testid="tiktok-embed"
        cite={episodeUrl}
        data-video-id={episodeGuid.replace("tiktok_", "")}
        style={{
          maxWidth: 325,
          minWidth: 225,
          minHeight: 640,
          margin: 0,
        }}
      >
        <section />
      </blockquote>
    </div>
  );
}

function TiktokCover({ episodeGuid, episodeUrl }: TiktokCoverProps) {
  const { data: tiktokEmbed } = useQuery({
    queryKey: ["tiktok-covers", episodeGuid],
    queryFn: async () => {
      const cachedTiktokEmbed = sessionStorage.getItem(`tiktok-${episodeUrl}`);

      if (cachedTiktokEmbed) {
        return JSON.parse(cachedTiktokEmbed);
      }

      const tiktokEmbed = await getTiktokEmbed(episodeUrl);

      if (tiktokEmbed) {
        sessionStorage.setItem(
          `tiktok-${episodeUrl}`,
          JSON.stringify(tiktokEmbed)
        );

        return tiktokEmbed;
      }
    },
    enabled: Boolean(episodeGuid && episodeUrl),
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="relative w-20 lg:w-40 aspect-9/16 bg-muted rounded-md overflow-hidden"
        >
          {tiktokEmbed && (
            <img
              className="size-full object-cover object-center"
              src={tiktokEmbed.thumbnail_url}
              loading="lazy"
            />
          )}

          <PlayIcon className="fill-white stroke-white absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 size-9 drop-shadow" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <TiktokPlayer episodeGuid={episodeGuid} episodeUrl={episodeUrl} />
      </DialogContent>
    </Dialog>
  );
}

type InstagramCoverProps = {
  episodeUrl: MentionCard["episode"]["public_url"];
};

let instagramIsInititalized = "";

function InstagramCover({ episodeUrl }: InstagramCoverProps) {
  const { location } = useHistory();

  useEffect(() => {
    if (instagramIsInititalized !== location.pathname) {
      try {
        // @ts-expect-error globally defined
        window.instgrm?.Embeds.process();
      } catch {
        /* empty */
      }
    }
    instagramIsInititalized = location.pathname;
  }, [location]);

  return (
    <div className="relative bg-muted rounded-md aspect-square w-20 lg:w-40 overflow-hidden">
      <div className="ig-embed">
        <blockquote
          className="instagram-media instagram-embed"
          data-instgrm-permalink={`${episodeUrl}?utm_source=ig_embed`}
          data-instgrm-version="14"
        />
      </div>
    </div>
  );
}

type YoutubePlayerProps = {
  youtubeId: EpisodeV4["youtube_id"];
  firstMentionTime: MentionV4["timing"];
};

function YoutubePlayer({ youtubeId, firstMentionTime }: YoutubePlayerProps) {
  const startTime = firstMentionTime ?? 0;

  return (
    <iframe
      data-testid="youtube-embed"
      width="100%"
      height="400"
      src={`https://www.youtube.com/embed/${youtubeId}?start=${startTime}&autoplay=1`}
      title="YouTube video player"
      frameBorder="0"
      style={{ borderRadius: 4 }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    ></iframe>
  );
}

type YoutubeCoverProps = {
  artworkUrl?: string;
  youtubeId: EpisodeV4["youtube_id"];
  firstMentionTime: MentionV4["timing"];
};

function YoutubeCover({
  artworkUrl,
  youtubeId,
  firstMentionTime,
}: YoutubeCoverProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="youtube-embed-open"
          type="button"
          className="relative aspect-video w-32 lg:w-70 bg-muted rounded-md overflow-hidden"
        >
          {artworkUrl && (
            <img
              className="size-full object-cover object-center"
              loading="lazy"
              src={artworkUrl}
            />
          )}

          <PlayIcon className="fill-white stroke-white absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 size-9 drop-shadow" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <YoutubePlayer
          firstMentionTime={firstMentionTime}
          youtubeId={youtubeId}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function Cover({ mentionCard }: CoverProps) {
  const artworkUrl =
    mentionCard.episode.artwork_url || mentionCard.channel.artwork_url;

  return mentionCard.channel.channel_type === SOURCE.YOUTUBE ? (
    <YoutubeCover
      firstMentionTime={mentionCard.mentions[0]?.timing}
      youtubeId={mentionCard.episode.youtube_id}
      artworkUrl={artworkUrl}
    />
  ) : artworkUrl ? (
    <img
      className={cn(
        "object-cover object-center rounded-md",
        mentionCard.channel.channel_type === SOURCE.TV ||
          mentionCard.channel.channel_type === SOURCE.WEB_TV ||
          mentionCard.channel.channel_type === SOURCE.NEWS
          ? "aspect-video w-32 lg:w-70"
          : "aspect-square w-20 lg:w-40"
      )}
      loading="lazy"
      src={artworkUrl}
    />
  ) : mentionCard.channel.channel_type === SOURCE.TIKTOK ? (
    <TiktokCover
      episodeGuid={mentionCard.episode.guid}
      episodeUrl={mentionCard.episode.public_url}
    />
  ) : mentionCard.channel.channel_type === SOURCE.INSTAGRAM ? (
    <InstagramCover episodeUrl={mentionCard.episode.public_url} />
  ) : (
    <div className="aspect-square bg-muted w-20 lg:w-40" />
  );
}
